import { useEffect, useState, useCallback, useRef } from 'react'
import { lumi } from '@/lib/lumi'
import {Save, Loader2, Upload, X, ImageIcon, ChevronDown, ChevronRight} from 'lucide-react'
import toast from 'react-hot-toast'
import type { SiteSetting } from '@/types'
import { useAuth } from '@/hooks/useAuth'

const TEAL = '#5BBDB5'
const TEAL_BG = '#DCF0EE'

interface Field { key: string; label: string; placeholder: string; type?: 'text' | 'textarea' | 'url'; hint?: string }
interface Section { id: string; title: string; emoji: string; fields: Field[] }

const SECTIONS: Section[] = [
  {
    id: 'identity', title: 'Identidade e SEO', emoji: '🏛',
    fields: [
      { key: 'site_title', label: 'Nome do Site', placeholder: 'Palácio da Sabedoria de São José do Seridó', hint: 'Exibido no cabeçalho e aba do navegador.' },
      { key: 'meta_description', label: 'Meta Descrição (SEO)', placeholder: 'Palácio da Sabedoria — acolhimento de idosos...', type: 'textarea', hint: 'Máx. 160 caracteres para o Google.' },
    ],
  },
  {
    id: 'pix', title: 'Doações via PIX', emoji: '💚',
    fields: [
      { key: 'pix_key', label: 'Chave PIX', placeholder: 'CPF, CNPJ, e-mail ou chave aleatória', hint: 'Usada no modal de doação gerado automaticamente.' },
      { key: 'whatsapp_supporter', label: 'WhatsApp para Apoiador Mensal', placeholder: '5584999999999', hint: 'Número com código do país e DDD, sem espaços. Ex: 5584999999999. O visitante será direcionado ao WhatsApp com a mensagem pré-definida.' },
    ],
  },
  {
    id: 'hero', title: 'Banner Principal (Hero)', emoji: '🖼',
    fields: [
      { key: 'hero_title', label: 'Título do Hero', placeholder: 'Um lar de amor, respeito e dignidade...', type: 'textarea' },
      { key: 'hero_subtitle', label: 'Subtítulo do Hero', placeholder: 'Há mais de 15 anos oferecendo...', type: 'textarea' },
    ],
  },
  {
    id: 'stats', title: 'Estatísticas', emoji: '📊',
    fields: [
      { key: 'stat_residents', label: 'Nº de Residentes', placeholder: '87' },
      { key: 'stat_professionals', label: 'Nº de Profissionais', placeholder: '24' },
      { key: 'stat_3_label', label: 'Estatística 3 — Rótulo', placeholder: 'ATENDIMENTO' },
      { key: 'stat_3_value', label: 'Estatística 3 — Valor', placeholder: '24 HORAS' },
      { key: 'stat_4_label', label: 'Tempo de Atuação — Valor', placeholder: 'MAIS DE 15 ANOS' },
      { key: 'stat_4_suffix', label: 'Tempo de Atuação — Sufixo', placeholder: 'DE ATUAÇÃO' },
    ],
  },
  {
    id: 'about', title: 'Sobre o Palácio da Sabedoria de São José do Seridó', emoji: '🏡',
    fields: [
      { key: 'about_title', label: 'Título da Seção', placeholder: 'Sobre o Palácio da Sabedoria de São José do Seridó' },
      { key: 'mission_text', label: 'Nossa Missão', placeholder: 'Promover qualidade de vida, dignidade...', type: 'textarea' },
      { key: 'vision_text', label: 'Nossa Visão', placeholder: 'Ser referência regional...', type: 'textarea' },
      { key: 'values_text', label: 'Nossos Valores (um por linha)', placeholder: 'Respeito\nAmor\nTransparência\nHumanização\nÉtica', type: 'textarea' },
    ],
  },
  {
    id: 'structure', title: 'Estrutura — Nomes dos Espaços', emoji: '🏢',
    fields: [
      { key: 'struct_label_1', label: 'Nome do Espaço 1', placeholder: 'Quartos Climatizados' },
      { key: 'struct_label_2', label: 'Nome do Espaço 2', placeholder: 'Jardim Terapêutico' },
      { key: 'struct_label_3', label: 'Nome do Espaço 3', placeholder: 'Fisioterapia' },
      { key: 'struct_label_4', label: 'Nome do Espaço 4', placeholder: 'Área de Convivência' },
      { key: 'struct_label_5', label: 'Nome do Espaço 5', placeholder: 'Refeitório' },
      { key: 'struct_label_6', label: 'Nome do Espaço 6', placeholder: 'Enfermaria 24h' },
    ],
  },
  {
    id: 'instagram', title: 'Acompanhe Nosso Dia a Dia (Instagram)', emoji: '📱',
    fields: [
      { key: 'instagram_url', label: 'URL do Instagram', placeholder: 'https://instagram.com/...', type: 'url' },
      { key: 'instagram_handle', label: 'Handle do Instagram', placeholder: '@palaciodosidosos' },
      {
        key: 'instagram_embed_code',
        label: '📲 Incorporação Behold',
        placeholder: '<div id="behold-widget-XXXX"></div>\n<script src="https://w.behold.so/widget.js" type="module"></script>',
        type: 'textarea',
        hint: 'Cole aqui o código de incorporação gerado pelo Behold (behold.so). Acesse behold.so → conecte seu Instagram → copie o "Embed Code" e cole neste campo. O feed será exibido automaticamente na seção "Acompanhe Nosso Dia a Dia" do site.',
      },
    ],
  },
  {
    id: 'help', title: 'Como Ajudar', emoji: '🙌',
    fields: [
      { key: 'how_to_help_title', label: 'Título da Seção', placeholder: 'Como você pode ajudar' },
      { key: 'help_1_label', label: 'Ajuda 1 — Título', placeholder: 'Doação Financeira' },
      { key: 'help_1_desc', label: 'Ajuda 1 — Descrição', placeholder: 'Toda contribuição faz a diferença!' },
      { key: 'help_2_label', label: 'Ajuda 2 — Título', placeholder: 'Doação de Alimentos' },
      { key: 'help_2_desc', label: 'Ajuda 2 — Descrição', placeholder: 'Alimentos não perecíveis...' },
      { key: 'help_3_label', label: 'Ajuda 3 — Título', placeholder: 'Doação de Medicamentos' },
      { key: 'help_3_desc', label: 'Ajuda 3 — Descrição', placeholder: 'Medicamentos devem estar dentro do prazo...' },
      { key: 'help_4_label', label: 'Ajuda 4 — Título', placeholder: 'Trabalho Voluntário' },
      { key: 'help_4_desc', label: 'Ajuda 4 — Descrição', placeholder: 'Seu tempo e carinho transformam vidas.' },
    ],
  },
  {
    id: 'contact', title: 'Contato e Rodapé', emoji: '📞',
    fields: [
      { key: 'contact_title', label: 'Título da Seção Contato', placeholder: 'Entre em contato' },
      { key: 'footer_address', label: 'Endereço', placeholder: 'Rua das Acácias, 123, São José do Seridó – RN', type: 'textarea' },
      { key: 'footer_phone', label: 'Telefone', placeholder: '(84) 0000-0000' },
      { key: 'footer_email', label: 'E-mail', placeholder: 'contato@palaciodosabedoria.org.br' },
      { key: 'footer_about', label: 'Texto de Apresentação (Rodapé)', placeholder: 'Palácio da Sabedoria de São José do Seridó...', type: 'textarea' },
      { key: 'footer_copyright', label: 'Copyright', placeholder: '© 2026 Palácio da Sabedoria. Todos os direitos reservados.' },
      { key: 'footer_privacy_link', label: 'Link — Política de Privacidade', placeholder: 'https://...', type: 'url' },
      { key: 'footer_terms_link', label: 'Link — Termos de Uso', placeholder: 'https://...', type: 'url' },
    ],
  },
]

const IMAGE_GROUPS = [
  {
    id: 'branding', title: 'Identidade Visual', emoji: '🎨',
    items: [
      { key: 'logo_image', label: 'Logo da Instituição', hint: 'PNG/WebP com fundo transparente. Altura mínima 72px.' },
      { key: 'about_image', label: 'Foto da Instituição (Seção Sobre)', hint: 'Foto principal da seção Sobre.' },
    ],
  },
  {
    id: 'hero_images', title: 'Banners do Hero (até 4)', emoji: '🖼',
    items: [
      { key: 'header_image',  label: 'Banner 1', hint: 'Largura mínima 1440px. Proporção 1440×560 recomendada.' },
      { key: 'hero_image_2', label: 'Banner 2', hint: 'Opcional. Mesma proporção do Banner 1.' },
      { key: 'hero_image_3', label: 'Banner 3', hint: 'Opcional.' },
      { key: 'hero_image_4', label: 'Banner 4', hint: 'Opcional.' },
    ],
  },
  {
    id: 'structure', title: 'Fotos da Estrutura', emoji: '🏢',
    items: Array.from({ length: 6 }, (_, i) => ({
      key: `struct_img_${i + 1}`,
      label: `Foto ${i + 1}`,
      hint: 'Apenas fotos com imagem cadastrada são exibidas.',
    })),
  },

]

export default function LandingSettings() {
  const { user } = useAuth()
  const [values, setValues] = useState<Record<string, string>>({})
  const [ids, setIds] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [uploadingKey, setUploadingKey] = useState<string | null>(null)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ identity: true, pix: true })
  const imageRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const allKeys = [
        ...SECTIONS.flatMap(s => s.fields.map(f => f.key)),
        ...IMAGE_GROUPS.flatMap(g => g.items.map(i => i.key)),
      ]
      const { list } = await lumi.entities.site_settings.list({ filter: { key: { $in: allKeys } }, limit: 150 })
      const vals: Record<string, string> = {}
      const idMap: Record<string, string> = {}
      ;(list as SiteSetting[]).forEach(s => { vals[s.key] = s.value; idMap[s.key] = s._id })
      setValues(vals)
      setIds(idMap)
    } catch {
      toast.error('Erro ao carregar configurações.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const saveSetting = async (key: string, value: string, category: string) => {
    const now = new Date().toISOString()
    if (ids[key]) {
      await lumi.entities.site_settings.update(ids[key], { value, updatedAt: now })
    } else {
      const created = await lumi.entities.site_settings.create({
        key, value, category, creator: user?.userId || '', createdAt: now, updatedAt: now,
      })
      setIds(prev => ({ ...prev, [key]: (created as SiteSetting)._id }))
    }
  }

  const handleSaveField = async (field: Field, category: string) => {
    const val = values[field.key] ?? ''
    setSaving(field.key)
    try {
      await saveSetting(field.key, val, category)
      toast.success(`"${field.label}" salvo!`)
    } catch {
      toast.error('Erro ao salvar.')
    } finally {
      setSaving(null)
    }
  }

  const handleImageUpload = async (key: string, file: File, category = 'media') => {
    if (!file.type.startsWith('image/')) { toast.error('Apenas imagens.'); return }
    setUploadingKey(key)
    try {
      const result = await lumi.tools.file.upload([file])
      const url = result?.[0]?.fileUrl || result?.[0]?.url || ''
      if (!url) throw new Error('URL não retornada')
      await saveSetting(key, url, category)
      setValues(prev => ({ ...prev, [key]: url }))
      toast.success('Imagem salva!')
    } catch {
      toast.error('Erro ao fazer upload.')
    } finally {
      setUploadingKey(null)
    }
  }

  const handleImageRemove = async (key: string) => {
    if (!ids[key]) return
    try {
      await lumi.entities.site_settings.update(ids[key], { value: '', updatedAt: new Date().toISOString() })
      setValues(prev => ({ ...prev, [key]: '' }))
      toast.success('Imagem removida.')
    } catch { toast.error('Erro ao remover.') }
  }

  const toggleSection = (id: string) =>
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }))

  const renderField = (field: Field, category: string) => (
    <div key={field.key} className="bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4">
      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-0.5">{field.label}</label>
      {field.hint && <p className="text-xs text-gray-400 mb-2">{field.hint}</p>}
      <div className="flex gap-2 items-start">
        {field.type === 'textarea' ? (
          <textarea
            value={values[field.key] ?? ''}
            onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
            placeholder={field.placeholder}
            rows={3}
            className="flex-1 px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none resize-none transition-colors"
            onFocus={e => (e.currentTarget.style.boxShadow = '0 0 0 2px rgba(91,189,181,0.35)')}
            onBlur={e => (e.currentTarget.style.boxShadow = '')}
          />
        ) : (
          <input
            type={field.type || 'text'}
            value={values[field.key] ?? ''}
            onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
            placeholder={field.placeholder}
            className="flex-1 px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none transition-colors"
            onFocus={e => (e.currentTarget.style.boxShadow = '0 0 0 2px rgba(91,189,181,0.35)')}
            onBlur={e => (e.currentTarget.style.boxShadow = '')}
          />
        )}
        <button
          onClick={() => handleSaveField(field, category)}
          disabled={saving === field.key}
          className="px-3.5 py-2.5 bg-gray-900 dark:bg-gray-700 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center gap-1.5 flex-shrink-0"
        >
          {saving === field.key ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Salvar
        </button>
      </div>
    </div>
  )

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configurações da Landing Page</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Edite todos os textos, imagens e informações exibidos na página inicial do site.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin" style={{ color: TEAL }} />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Seções de texto */}
          {SECTIONS.map(section => (
            <div key={section.id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{section.emoji}</span>
                  <span className="font-bold text-gray-900 dark:text-white text-sm">{section.title}</span>
                  <span className="text-xs text-gray-400">({section.fields.length} campo{section.fields.length > 1 ? 's' : ''})</span>
                </div>
                {openSections[section.id] ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
              </button>
              {openSections[section.id] && (
                <div className="px-5 pb-5 space-y-3 border-t border-gray-100 dark:border-gray-800 pt-4">
                  {section.fields.map(f => renderField(f, section.id))}
                </div>
              )}
            </div>
          ))}

          {/* Grupos de imagens */}
          {IMAGE_GROUPS.map(group => (
            <div key={group.id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
              <button
                onClick={() => toggleSection(`img_${group.id}`)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{group.emoji}</span>
                  <span className="font-bold text-gray-900 dark:text-white text-sm">{group.title}</span>
                </div>
                {openSections[`img_${group.id}`] ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
              </button>
              {openSections[`img_${group.id}`] && (
                <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-800 pt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {group.items.map(item => {
                      const imgUrl = values[item.key] || ''
                      const isUp = uploadingKey === item.key
                      return (
                        <div key={item.key} className="bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-3">
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 truncate">{item.label}</p>
                          {item.hint && <p className="text-xs text-gray-400 mb-2 leading-tight">{item.hint}</p>}
                          <div
                            className="relative rounded-lg overflow-hidden mb-2 cursor-pointer flex items-center justify-center"
                            style={{ aspectRatio: '1/1', background: TEAL_BG }}
                            onClick={() => !imgUrl && imageRefs.current[item.key]?.click()}
                          >
                            {imgUrl ? (
                              <>
                                <img src={imgUrl} alt={item.label} className="w-full h-full object-cover" />
                                <button
                                  onClick={e => { e.stopPropagation(); handleImageRemove(item.key) }}
                                  className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full shadow"
                                >
                                  <X size={10} />
                                </button>
                              </>
                            ) : (
                              <div className="flex flex-col items-center gap-1 text-center px-2">
                                {isUp ? <Loader2 size={20} className="animate-spin" style={{ color: TEAL }} /> : <ImageIcon size={20} style={{ color: '#8DD0CC' }} />}
                                <span className="text-xs text-gray-400">{isUp ? 'Enviando...' : 'Clique para adicionar'}</span>
                              </div>
                            )}
                          </div>
                          <input
                            ref={el => { imageRefs.current[item.key] = el }}
                            type="file" accept="image/*" className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(item.key, f, group.id); e.target.value = '' }}
                          />
                          {imgUrl && (
                            <button
                              onClick={() => imageRefs.current[item.key]?.click()}
                              disabled={isUp}
                              className="w-full flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-semibold text-white transition-all disabled:opacity-50"
                              style={{ background: TEAL }}
                            >
                              {isUp ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                              Trocar
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
